import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const AdminLogin = () => {
  const { login, token, admin } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Admin Login | Hospitality Hub";
    if (token && admin) navigate("/admin/quotes", { replace: true });
  }, [token, admin, navigate]);

  const formatError = (detail) => {
    if (!detail) return "Unable to sign in. Please try again.";
    if (typeof detail === "string") return detail;
    return "Unable to sign in. Please try again.";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/quotes", { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#0E0E0E] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/10">
            <Lock className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Private Admin</p>
            <p className="font-serif-display text-xl">Hospitality Hub</p>
          </div>
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl leading-tight">
          Sign in to your quote inbox.
        </h1>
        <p className="mt-3 text-white/60 text-sm leading-relaxed">
          This dashboard is restricted to authorised Hospitality Hub staff.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-white/60">Admin Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm placeholder:text-white/30 transition-colors"
              placeholder="you@hospitalityhubus.com"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-white/60">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-sm placeholder:text-white/30 transition-colors"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-xs text-red-400 uppercase tracking-[0.16em]">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-50 text-white px-8 py-4 uppercase tracking-[0.22em] text-xs transition-colors"
          >
            <LogIn className="w-4 h-4" /> {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-10 text-[10px] uppercase tracking-[0.2em] text-white/30">
          © {new Date().getFullYear()} Hospitality Hub · Admin Access
        </p>
      </div>
    </main>
  );
};

export default AdminLogin;
